import _round from 'lodash/round';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { LightningBoltIcon } from '@/components/icons/lightning-bolt';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useLastAutomationChannel } from '@/hooks/use-last-automation-channel';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { cn } from '@/utils/classnames';
import { isWithinLastDays, toDate } from '@/utils/date-util';

import { PerformanceStatusBanner } from '../performance-status-banner';
import { BUDGET_THRESHOLD_PERCENTAGE } from './constants';
import {
  dismissalKey,
  isDismissalActive,
  omitAutomationBannerDismissals,
  staleKeysForTransition,
  useAutomationBannerState,
} from './use-automation-banner-state';
import { useAutomationMessages } from './use-automation-messages';

const BANNER_DISMISSAL_LOOKBACK_DAYS = 5;

function AutomationStatusBanner() {
  const { setBudgetAutomationBannerColor } = useOpeningContext();
  const openingSlug = useOpeningSlug();
  // `useLastAutomationChannel` kicks off its sibling fetch before suspending,
  // so call it first to keep the channels-list query in parallel with the
  // funnel-details API.
  const channel = useLastAutomationChannel();
  const { opening, hiringGoal, analyticsData } = useOpeningDetails();
  const isCurrentHiringGoal = hiringGoal?.is_current ?? false;
  const { getAutomationMessages } = useAutomationMessages();
  const { openEditTargetsDialog } = useEditTargetsContext();
  const { automationBannerClosed, setAutomationBannerClosed } =
    useAutomationBannerState();

  const lastAutomationReason = channel?.deactivation_reason ?? null;
  const isActive = !channel?.deactivated_at;
  const lastAutomationTriggerAt = toDate(
    channel?.automation_triggered_at ?? null,
  );
  const bannerColor = isActive ? 'blue' : 'rose';

  /* Open edit targets dialog on click */
  const handleEditTargets = useCallback(() => {
    openEditTargetsDialog({
      targets: {
        conversion: analyticsData?.conversion_stats?.expected ?? null,
        budget: analyticsData?.budget_stats?.expected ?? null,
        cpa: analyticsData?.cpa_stats?.expected ?? null,
      },
      spendStats: analyticsData?.spend_stats,
    });
  }, [openEditTargetsDialog, analyticsData]);

  /* Check if the spend has breached the 80% threshold of the budget, and is less than the budget */
  const { hasSpendBreachedThreshold, spendPercentage } = useMemo(() => {
    const budget = analyticsData?.budget_stats?.expected ?? 0;
    const spend = analyticsData?.spend_stats?.total ?? 0;

    if (!budget || !spend)
      return { hasSpendBreachedThreshold: false, spendPercentage: undefined };

    const hasSpendBreachedThreshold =
      spend < budget && spend > budget * BUDGET_THRESHOLD_PERCENTAGE;
    const spendPercentage = hasSpendBreachedThreshold
      ? _round((spend / budget) * 100, 1)
      : undefined;

    return { hasSpendBreachedThreshold, spendPercentage };
  }, [analyticsData]);

  const automationReason = useMemo(
    () =>
      !lastAutomationReason && hasSpendBreachedThreshold
        ? 'target_budget_breached'
        : lastAutomationReason,
    [lastAutomationReason, hasSpendBreachedThreshold],
  );

  const currentDismissalKey = useMemo(
    () => dismissalKey(automationReason, isActive),
    [automationReason, isActive],
  );

  const isDismissed = useMemo(() => {
    if (currentDismissalKey == null) return false;
    const byReason = automationBannerClosed[openingSlug];
    return isDismissalActive(byReason?.[currentDismissalKey]);
  }, [automationBannerClosed, openingSlug, currentDismissalKey]);

  /* Set the banner color for budget automation banner. This is used to determine the color of the budget card in the analytics section.
   * Show the banner only if following conditions are met:
    - The banner is not dismissed AND
    - The hiring goal is current AND
    - The spend has breached the threshold OR
    - The last automation reason is 'target_budget_met' AND has triggered in the last 5 days
   */
  useEffect(() => {
    if (
      !isDismissed &&
      isCurrentHiringGoal &&
      (hasSpendBreachedThreshold ||
        (lastAutomationReason === 'target_budget_met' &&
          lastAutomationTriggerAt &&
          isWithinLastDays(
            lastAutomationTriggerAt,
            BANNER_DISMISSAL_LOOKBACK_DAYS,
          )))
    ) {
      setBudgetAutomationBannerColor(bannerColor);
    } else {
      setBudgetAutomationBannerColor(undefined);
    }
  }, [
    isDismissed,
    hasSpendBreachedThreshold,
    bannerColor,
    isCurrentHiringGoal,
    lastAutomationReason,
    lastAutomationTriggerAt,
    setBudgetAutomationBannerColor,
  ]);

  /* Get automation messages */
  const automationCopy = useMemo(() => {
    const copy = getAutomationMessages(
      automationReason,
      lastAutomationTriggerAt,
      spendPercentage,
    );

    if (!copy) return null;

    const title = isActive ? copy.activeTitle : copy.pausedTitle;
    const description = isActive
      ? copy.activeDescription
      : copy.pausedDescription;

    return {
      title,
      description,
    };
  }, [
    automationReason,
    getAutomationMessages,
    isActive,
    lastAutomationTriggerAt,
    spendPercentage,
  ]);

  /* Button logic */
  const button = useMemo(() => {
    if (
      lastAutomationReason === 'hiring_goal_ended' &&
      !isActive &&
      hiringGoal?.url
    ) {
      return (
        <Button variant="secondary" asChild>
          <Link to={hiringGoal.url} target="_blank">
            <FormattedMessage
              defaultMessage="Set new hiring goal"
              description="CTA when automation ended the old goal: open Hire to create a new one"
            />
          </Link>
        </Button>
      );
    }

    if (lastAutomationReason === 'target_budget_met' && !isActive) {
      return (
        <Button variant="secondary" onClick={handleEditTargets}>
          <FormattedMessage
            defaultMessage="Update Budget"
            description="CTA when spend target was hit: open edit targets for new budget"
          />
        </Button>
      );
    }

    return null;
  }, [lastAutomationReason, isActive, hiringGoal?.url, handleEditTargets]);

  const closeBanner = useCallback(() => {
    if (currentDismissalKey == null) return;
    setAutomationBannerClosed(prev => ({
      ...prev,
      [openingSlug]: {
        ...(prev[openingSlug] ?? {}),
        [currentDismissalKey]: Date.now(),
      },
    }));
  }, [currentDismissalKey, openingSlug, setAutomationBannerClosed]);

  /* Campaign reactivated or paused: clear stale dismissals for the opposite state. */
  useEffect(() => {
    const keysToRemove = staleKeysForTransition(isActive);
    setAutomationBannerClosed(prev => {
      const opening = prev[openingSlug];
      if (!opening) return prev;
      const hasStale = keysToRemove.some(k => k in opening);
      if (!hasStale) return prev;
      return omitAutomationBannerDismissals(prev, openingSlug, keysToRemove);
    });
  }, [isActive, openingSlug, setAutomationBannerClosed]);

  /* For past hiring goal we should not show funnel/budget automation messages. We should show the hiring goal status banner instead. <PerformanceStatusBanner /> */
  if (!isCurrentHiringGoal && lastAutomationReason !== 'hiring_goal_ended') {
    return <PerformanceStatusBanner />;
  }

  /* If the automation was not triggered in the last 5 days, we should not show the banner. */
  if (
    !hasSpendBreachedThreshold &&
    (!lastAutomationTriggerAt ||
      !isWithinLastDays(
        lastAutomationTriggerAt,
        BANNER_DISMISSAL_LOOKBACK_DAYS,
      ))
  ) {
    return null;
  }

  /* For past hiring goal we should not show hiring automation message if there is an active hiring goal present. */
  if (
    lastAutomationReason === 'hiring_goal_ended' &&
    !isActive &&
    opening.hasActiveHiringGoal
  ) {
    return null;
  }

  /* If there is no automation copy, we should not show the banner. */
  if (!automationCopy) {
    return null;
  }

  if (isDismissed) {
    return null;
  }

  return (
    <Banner
      color={bannerColor}
      title={automationCopy.title}
      description={automationCopy.description}
      button={button}
      onClose={
        currentDismissalKey != null &&
        automationReason !== 'hiring_goal_ended' &&
        currentDismissalKey !== 'target_budget_met:paused'
          ? closeBanner
          : undefined
      }
      icon={
        <LightningBoltIcon
          className={cn('h-4 w-4', isActive ? 'text-fx-blue' : 'text-fx-rose')}
        />
      }
    />
  );
}

export { AutomationStatusBanner };
